import _jsx from '@babel/runtime/helpers/esm/jsx';
import { memo, Children, createContext, useContext } from 'react';
import { createSimpleSelector, useMakeSelect } from '@dbh/reselect-extra';
import _styled from 'styled-components';
import { IntlProvider, useIntl, FormattedMessage } from 'react-intl';
import '@dbh/environment';
import Spinner from '@dbh/spinner';
import { createHydratableComponent } from '@dbh/hydration';
import { Map, isImmutable } from 'immutable';
import 'prop-types';
import 'react-immutable-proptypes';
import withConformsTo from '@dbh/with-conforms-to-for-production-www';
import _startsWith from 'lodash/startsWith';
import { createSelector } from 'reselect';
import { makeSelectCountry, makeSelectLocale } from '@dbh/routing-redux';
import { makeSelectDevMode } from '@dbh/development-redux';
import { memoize, mapContainsMapValue, plainObjectContainsPlainObjectValue } from '@dbh/lodash-extra';
import _noop from 'lodash/noop';
import '@dbh/create-custom-react-prop-type';
import '@dbh/react-extra';

// of `LanguageProvider` is always injected in server side rendering.
// @see `@dbh/create-reducer-redux`.
const REDUX_AND_SAGA_KEY="language";const LOAD_MESSAGES_SUCCESS="@dbh/language-provider-redux/LOAD_MESSAGES_SUCCESS";const LOAD_MESSAGES_ERROR="@dbh/language-provider-redux/LOAD_MESSAGES_ERROR";const CLEAR_UNUSED_TRANSLATIONS_INVOKED="@dbh/language-provider-redux/CLEAR_UNUSED_TRANSLATIONS_INVOKED";const POSSIBLE_START_OF_TRANSLATION_IDS=Object.freeze(["components.","containers.","global.","datetime.","packages.",...[]]).filter(Boolean);

/**
 * Returns the slice of the state that contains the `LanguageProvider` state.
 * @param {Immutable.Map} state `redux` State.
 * @return {Immutable.Map?} .
 */const selectLanguage=a=>a.get(REDUX_AND_SAGA_KEY);var selectLanguage$1 = selectLanguage;

/**
 * Returns the slice of the state that contains the loading status of the
 * messages in the various sites (combinations of countries and locales).
 * @return {Immutable.Map?} .
 */const makeSelectMessagesLoadedAll=()=>createSimpleSelector(selectLanguage$1,["messagesLoaded"]),makeSelectMessagesLoaded=()=>createSelector(makeSelectCountry(),makeSelectLocale(),makeSelectMessagesLoadedAll(),(a,b,c)=>!!c.getIn([a,b]));/**
 * Returns a selector that returns the load status of the messages (localized
 * strings).
 * @return {boolean} `true` if the messages are loaded.
 * @see {@link https://apidev.daybreakhotels.com/v1.0/IT/en-GB/messages}
 */var makeSelectMessagesLoaded$1 = makeSelectMessagesLoaded;

/**
 * Returns the slice of the state that contains the messages from the `API` in
 * the various sites (combinations of countries and locales).
 * @return {Immutable.Map?} .
 */const makeSelectMessagesAll=()=>createSimpleSelector(selectLanguage$1,["messages"]),makeSelectMessages=()=>createSelector(makeSelectCountry(),makeSelectLocale(),makeSelectMessagesLoaded$1(),makeSelectMessagesAll(),(a,b,c,d)=>c&&d.getIn([a,b]));/**
 * Returns a selector that returns the messages (localized strings) from the
 * `API`. They can be edited in the `admindev`.
 * @return {Immutable.Map?} .
 * @see {@link https://apidev.daybreakhotels.com/v1.0/IT/en-GB/messages}
 */var makeSelectMessages$1 = makeSelectMessages;

const countryCodePropType=_noop;var countryCodePropType$1 = countryCodePropType;

/**
 * Returns `true` if the input looks like a `react-intl` translation `ID`.
 * @param {string?} input .
 * @return {boolean} .
 */const isTranslationId=withConformsTo("isTranslationId",[],a=>!!a&&POSSIBLE_START_OF_TRANSLATION_IDS.some(b=>_startsWith(a,b)));var isTranslationId$1 = isTranslationId;

const translationIdIfIntlFormatterTruthyPropType=_noop;var translationIdIfIntlFormatterTruthyPropType$1 = translationIdIfIntlFormatterTruthyPropType;

/*
 *
 * `generateTranslationIdWithCountry`: `@dbh/language-provider-redux`.
 *
 *//**
 * Given a "translation ID" returns the corrispondent country specific localization.
 * @param {string} translationId Identifier of a localized message. @example
 * `components.lorem.ipsum`.
 * @param {string} country An `ISO3166-1` alpha 2 country code. @example `US`.
 * @return {string} The "translation ID" for that country. @example
 * `components.lorem.ipsum.US`.
 * @see {@link https://en.wikipedia.org/wiki/ISO_3166-1}
 */const generateTranslationIdWithCountry=(a,b)=>a+"."+b;var generateTranslationIdWithCountry$1 = generateTranslationIdWithCountry;

/**
 * Given an `Immutable.Map` that maps `translationIds` to localized strings,
 * returns the `object` messages suitable to be passed as messages `prop` to
 * `react-intl - IntlProvider`.
 * This function manages the "localization per country" logic:
 * a message can have a additional `country-specific` "translation ID" defined
 * this way in the admin: `components.lorem.ipsum`, `components.lorem.ipsum.GB`,
 * `components.lorem.ipsum.FR` (and other countries too). If a `country-specific`
 * message is not defined for the current country, we keep the "generic" one.
 * The `generateTranslationIdWithCountry` should be removed from this code when
 * the back end and web admin get "localization per country" support without
 * the current "convention".
 * @param {Immutable.Map} adminMessagesForLocale Maps `translationIds` to localizations.
 * @param {string} country An `ISO3166-1` alpha 2 country code.
 * @return {Object} A valid `react-intl - IntlProvider` messages `prop`.
 * @throws {Error} If `adminMessagesForLocale` is not an `Immutable.Map`, or
 * country is not a valid `ISO3166-1` alpha 2 country code.
 * @see {@link https://github.com/yahoo/react-intl/wiki/Components#intlprovider}
 * @see {@link https://en.wikipedia.org/wiki/ISO_3166-1}
 */const generateReactIntlMessages=memoize(withConformsTo("generateReactIntlMessages",[],(a,b)=>a.reduce((a,c,d,e)=>{const f=generateTranslationIdWithCountry$1(d,b);// @see {@link https://facebook.github.io/immutable-js/docs/#/Map/get}
// eslint-disable-next-line no-param-reassign
return a[d]=e.get(f,c),a},{})));var generateReactIntlMessages$1 = generateReactIntlMessages;

/**
 * Given an `Immutable.Map` that maps "translation IDs" to localized strings,
 * returns an `object` that maps "translation IDs" used by the components to
 * debug messages. The returned object can be passed as messages `prop` to
 * `react-intl - IntlProvider`.
 * @param {Immutable.Map} adminMessagesForLocale Maps "translation IDs" to
 * localizations.
 * @param {string} country An `ISO3166-1` alpha 2 country code.
 * @return {Object} A valid `react-intl - IntlProvider` messages `prop`.
 * @throws {Error} If `adminMessagesForLocale` is not an `Immutable.Map` or
 * country is not a valid `ISO3166-1` alpha 2 country code.
 * @see {@link https://github.com/yahoo/react-intl/wiki/Components#intlprovider}
 * @see {@link https://en.wikipedia.org/wiki/ISO_3166-1}
 */const generateDebugMessages=memoize(withConformsTo("generateReactIntlMessages",[],(a,b)=>{const c=generateReactIntlMessages$1(a,b);return Object.keys(c).reduce((d,e)=>{const f=a.get(e)===c[e]?e:generateTranslationIdWithCountry$1(e,b),g=a.get(f);// eslint-disable-next-line no-param-reassign
return d[e]=g+" ("+f+")",d},{})}));var generateDebugMessages$1 = generateDebugMessages;

/**
 * Returns a selector that returns the messages (localized strings) in the
 * `react-intl` format (JavaScript, not immutable).
 * @return {Object?} .
 */const makeSelectReactIntlMessages=()=>createSelector(makeSelectMessagesLoaded$1(),makeSelectMessages$1(),makeSelectCountry(),makeSelectDevMode(),(a,b,c,d)=>{if(!a)return;const e=d.get("messages"),f=e?generateDebugMessages$1:generateReactIntlMessages$1;return f(b,c)});var makeSelectReactIntlMessages$1 = makeSelectReactIntlMessages;

/**
 * Returns a selector that returns the `locale` of `LanguageProvider`.
 * @return {string} The locale @example `it-IT`.
 */const makeSelectLanguageProviderLocale=()=>createSimpleSelector(selectLanguage$1,["locale"]);var makeSelectLanguageProviderLocale$1 = makeSelectLanguageProviderLocale;

/**
 * Returns a selector that returns the `country` of `LanguageProvider`.
 * @return {string} The country @example `IT`.
 */const makeSelectLanguageProviderCountry=()=>createSimpleSelector(selectLanguage$1,["country"]);var makeSelectLanguageProviderCountry$1 = makeSelectLanguageProviderCountry;

var _StyledDiv2;const LanguageProvider$1=/*#__PURE__*/memo(a=>{let{children:b,locale:c,intlProviderKey:d,messagesLoaded:e,messages:f}=a;// WARNING: if you avoid unmounting the child components when the messages
// are not yet loaded, you absolutely have to test the `TrackingScriptsManager`
// container and the `canFirePixelsPageView` event, since you may break them.
return e?/*#__PURE__*/_jsx(IntlProvider,{locale:c,messages:f,textComponent:"span"},d,Children.only(b)):_StyledDiv2||(_StyledDiv2=/*#__PURE__*/_jsx(_StyledDiv,{},void 0,/*#__PURE__*/_jsx(Spinner,{visible:!0,size:"ff0"})))});LanguageProvider$1.displayName="HydratableLanguageProvider";const HydratableLanguageProvider=createHydratableComponent(LanguageProvider$1,{id:"dbh-language-provider",props:{notHydratable:["children","messages"]},// A better solution would be to store the props in the dom separated from the
// component, so that a wrapper element would not be needed for providers etc.
// Meanwhile this style will help not break the app layout.
// @see `@dbh/hydration/helpers/createHydratableComponent`.
wrapperElement:{style:{display:"inherit",width:"inherit",flexDirection:"inherit"}}});var HydratableLanguageProviderComponent = HydratableLanguageProvider;var _StyledDiv=/*#__PURE__*/_styled("div").withConfig({componentId:"sc-1t1ap4g-0"})(["display:flex;justify-content:center;align-items:center;width:100vw;height:100vh;"]);

const TranslationsCollectorContext=/*#__PURE__*/createContext(null);const createTranslationsCollector=()=>{const a={},b=(b,c)=>{a[b]=c;};// eslint-disable-next-line react/display-name
return {collectUsedTranslationIds:a=>/*#__PURE__*/_jsx(TranslationsCollectorContext.Provider,{value:b},void 0,a),getUsedTranslationIds:()=>a}};

const initialState=Map({// @example `{ 'it-IT': { lorem: 'uno', ...}, 'en-GB': { lorem: 'one', ...} }`.
locale:void 0,country:void 0,messages:Map(),messagesLoaded:Map()});const languageProviderReducer=function(a,b){switch(void 0===a&&(a=initialState),b.type){case LOAD_MESSAGES_SUCCESS:{const{payload:{messages:c,locale:d,country:e,fromCache:f}}=b,g=[e,d];return f?a.merge({locale:d,country:e}):a.withMutations(a=>a.merge({locale:d,country:e})// We can use `Map` because `messages` it's a plain JavaScript
// object with only one level (key, value).
.setIn(["messages",...g],Map(c)).setIn(["messagesLoaded",...g],!0))}case CLEAR_UNUSED_TRANSLATIONS_INVOKED:{const{payload:{usedTranslationIds:c}}=b,d=a.get("country"),e=a.get("locale");return a.updateIn(["messages",...[d,e]],a=>a.filter((a,b)=>c.includes(b)))}default:return a}};var languageProviderReducer$1 = languageProviderReducer;

/**
 * The return value is either `arg` (if non-Immutable), or `arg` converted to JS.
 * Because `redux-form` selector `getFormMeta` returns `Immutable.Map` while the
 * `redux-form` props that is being passed to `IntlFormField` contains `meta`
 * prop as `Object`.
 * @param {(Immutable.Map|Object)?} arg .
 * @return {Object?} .
 */const ensureVariableIsJS=withConformsTo("ensureVariableIsJS",[],a=>{const b=isImmutable(a)?a.toJS():a;return b});var ensureVariableIsJS$1 = ensureVariableIsJS;

/**
 * Dispatched when the messages are loaded by the request saga.
 * @param {Object} messages The messages data.
 * @param {Object} messages.messages .
 * @param {string} messages.country .
 * @param {string} messages.locale .
 * @param {boolean?} messages.fromCache .
 * @return {Object} .
 */const messagesLoaded=a=>{let{messages:b,country:c,locale:d,fromCache:e}=a;return {type:LOAD_MESSAGES_SUCCESS,payload:{messages:b,country:c,locale:d,fromCache:e}}};/**
 * Dispatched when loading the messages fails.
 * @param {Object} error .
 * @return {Object} .
 */const messagesLoadingError=a=>({type:LOAD_MESSAGES_ERROR,error:a});/**
 * Dispatched after the last server-side render to clear the state of unused translations.
 * @param {string[]} usedTranslationIds The translations that were marked as used
 * in the last render.
 * @return {Object} .
 */const clearUnusedTranslations=withConformsTo("clearUnusedTranslations",[],a=>({type:CLEAR_UNUSED_TRANSLATIONS_INVOKED,payload:{usedTranslationIds:a}}));

/**
 * If a "translation ID" and `intlFormatter: true` are passed, it returns it
 * translated and formatted with `intl`. Otherwise it returns the input as is.
 * @param {string?} translationIdOrNormalText .
 * @param {Object?} options .
 * @param {boolean?} options.intlFormatter .
 * @param {Immutable.Map|Object?} options.intlFormatterValues .
 * @return {string?} .
 */const useIntlFormat=withConformsTo("useIntlFormat",[],(a,b)=>{let{intlFormatter:c,intlFormatterValues:d}=b;const e=useIntl();if(!c||!a)return a;// If the Object `intlFormatterValues` has an Object as one of its values,
// we assume it is a React element/component and so use <FormattedMessage>,
// since `react-intl` `intl`, does not support components.
const f=isImmutable(d)?mapContainsMapValue(d):plainObjectContainsPlainObjectValue(d);let g=ensureVariableIsJS$1(d);return g&&(g=Object.keys(g).reduce((a,b)=>{const c=g[b];return a[b]=c,isTranslationId$1(c)&&(a[b]=e.formatMessage({id:c})),a},{})),f?/*#__PURE__*/_jsx(FormattedMessage,{id:a,values:g}):e.formatMessage({id:a},g)});var useIntlFormat$1 = useIntlFormat;

const LanguageProvider=/*#__PURE__*/memo(a=>{let{children:b}=a;// We do not use `makeSelectLocale` because we need to change the locale
// at the same time the messages change.
// Otherwise `react-router` has issues updating its internal state
// and the "language" switch doesn’t work.
const c=useMakeSelect(makeSelectLanguageProviderLocale$1),d=useMakeSelect(makeSelectLanguageProviderCountry$1),e=useMakeSelect(makeSelectMessagesLoaded$1),f=useMakeSelect(makeSelectReactIntlMessages$1),g=useContext(TranslationsCollectorContext);// This context exists only if the `react` tree was wrapped with the collector.
let h;return h=!!g?new Proxy({...f,// Replace the standard `toJSON` implementation with one that returns a simple
// object. This will avoid a bug where any code that stringifies the `messages`
// object would flag all translations as used.
toJSON(){return {NOT_SERIALIZABLE:!0}}},{// Accessing any property in the `messages` object will call this method,
// passing the object itself in `target` and the property name in `prop`.
// @example
// `<Text intlFormatter>components.SearchBox.where</Text>` will call
// `intl.formatMessage({id: 'components.SearchBox.where'})` which will
// in turn access the `messages` object, triggering the trap and calling
// `get(messages, 'components.SearchBox.where')`.
// @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy/handler/get}
get(a,b){return Object.prototype.hasOwnProperty.call(a,b)&&"toJSON"!==b&&g(b,a[b]),a[b]}}):f,/*#__PURE__*/_jsx(HydratableLanguageProviderComponent,{messages:h,messagesLoaded:e,intlProviderKey:d+"/"+c,locale:c},void 0,b)});LanguageProvider.displayName="LanguageProvider";const importLanguageProviderSagas=import('./index-0370afdd.js'/* webpackChunkName: 'dbh.language-provider-redux.sagas' */);

export { CLEAR_UNUSED_TRANSLATIONS_INVOKED, HydratableLanguageProviderComponent as HydratableLanguageProvider, LOAD_MESSAGES_ERROR, LOAD_MESSAGES_SUCCESS, POSSIBLE_START_OF_TRANSLATION_IDS, REDUX_AND_SAGA_KEY, TranslationsCollectorContext, clearUnusedTranslations, countryCodePropType$1 as countryCodePropType, createTranslationsCollector, LanguageProvider as default, ensureVariableIsJS$1 as ensureVariableIsJS, importLanguageProviderSagas, isTranslationId$1 as isTranslationId, languageProviderReducer$1 as languageProviderReducer, makeSelectLanguageProviderCountry$1 as makeSelectLanguageProviderCountry, makeSelectLanguageProviderLocale$1 as makeSelectLanguageProviderLocale, makeSelectMessages$1 as makeSelectMessages, makeSelectMessagesLoaded$1 as makeSelectMessagesLoaded, makeSelectReactIntlMessages$1 as makeSelectReactIntlMessages, messagesLoaded, messagesLoadingError, translationIdIfIntlFormatterTruthyPropType$1 as translationIdIfIntlFormatterTruthyPropType, useIntlFormat$1 as useIntlFormat };
